// MCP admin surface DTOs (Track:oauth / F6, ADR-0006).
//
// Backs the Admin shell "MCP Connections" card: who has connected the
// MCP surface, under which client, and what they can currently read. All
// camelCase (ADR-0003). CRITICAL: no token / hash / secret material EVER appears
// on the wire — a connection is identified by (userId, clientId), never by any
// token value. `livePersonas` is the CURRENT `users.personas` grant (the
// truthful "who can read our data" answer); the token-time grant is carried
// separately, labelled 'granted at connection'.

import { z } from 'zod';

/** Connection status derived from the token rows (access-window-aware). */
export const mcpConnectionStatusSchema = z.enum(['active', 'expired', 'revoked']);
export type McpConnectionStatus = z.infer<typeof mcpConnectionStatusSchema>;

/** One MCP connection = a (user, client) pair collapsing its rotation chain. */
export const mcpConnectionDtoSchema = z.object({
    userId: z.string(),
    userName: z.string().nullable(),
    userEmail: z.string(),
    clientId: z.string(),
    clientName: z.string().nullable(),
    /** The CURRENT users.personas grant — the live authority answer. */
    livePersonas: z.array(z.string()),
    /** The persona grant captured on the latest token row (informational). */
    grantedPersonasAtConnection: z.array(z.string()),
    personaAtConnection: z.string(),
    connectedAt: z.string(),
    lastUsedAt: z.string().nullable(),
    accessExpiresAt: z.string().nullable(),
    refreshExpiresAt: z.string().nullable(),
    status: mcpConnectionStatusSchema,
});
export type McpConnectionDto = z.infer<typeof mcpConnectionDtoSchema>;

/** One registered OAuth client (DCR row) plus its live-connection count. */
export const mcpClientDtoSchema = z.object({
    clientId: z.string(),
    clientName: z.string().nullable(),
    redirectUris: z.array(z.string()),
    tokenEndpointAuthMethod: z.string(),
    issuedAt: z.string(),
    /** Count of ACTIVE connections bound to this client — Remove is safe at 0. */
    liveConnectionCount: z.number(),
});
export type McpClientDto = z.infer<typeof mcpClientDtoSchema>;

export const mcpConnectionsResponseSchema = z.object({
    connections: z.array(mcpConnectionDtoSchema),
});
export type McpConnectionsResponse = z.infer<typeof mcpConnectionsResponseSchema>;

export const mcpClientsResponseSchema = z.object({
    clients: z.array(mcpClientDtoSchema),
});
export type McpClientsResponse = z.infer<typeof mcpClientsResponseSchema>;

/** Revoke a user's access — for one client, or (clientId omitted) all clients. */
export const mcpRevokeAccessRequestSchema = z.object({
    userId: z.string(),
    clientId: z.string().optional(),
});
export type McpRevokeAccessRequest = z.infer<typeof mcpRevokeAccessRequestSchema>;

export const mcpRemoveClientRequestSchema = z.object({
    clientId: z.string(),
});
export type McpRemoveClientRequest = z.infer<typeof mcpRemoveClientRequestSchema>;

export const mcpAdminMutationResponseSchema = z.object({
    ok: z.boolean(),
    revokedCount: z.number(),
});
export type McpAdminMutationResponse = z.infer<typeof mcpAdminMutationResponseSchema>;
